import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin, isHod, canManageCrmRoles } from '../../utils/crmPermissions';
import { ArrowLeft } from 'lucide-react';

const MATRIX_ACTIONS = ['read', 'create', 'update', 'delete', 'export', 'import', 'approve', 'assign', 'manage'];

export default function CrmRolePermissions() {
  const { id } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const canEdit = isSuperAdmin(user) || isHod(user) || canManageCrmRoles(user);
  const [role, setRole] = useState(null);
  const [matrix, setMatrix] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/crm/roles/${id}/permissions`);
      const data = res.data?.data || {};
      setRole(data.role || null);
      setMatrix(data.matrix || []);
      const next = new Set();
      (data.matrix || []).forEach((mod) => {
        Object.values(mod.actions || {}).forEach((cell) => {
          if (cell.allowed) next.add(Number(cell.permission_id));
        });
      });
      setSelected(next);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load matrix', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const toggle = (permissionId) => {
    if (!canEdit) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) next.delete(permissionId);
      else next.add(permissionId);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/crm/roles/${id}/permissions`, { permission_ids: Array.from(selected) });
      toast({ title: 'Permissions saved' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const actionCols = useMemo(() => {
    const present = new Set();
    matrix.forEach((mod) => {
      Object.keys(mod.actions || {}).forEach((a) => present.add(a));
    });
    return MATRIX_ACTIONS.filter((a) => present.has(a));
  }, [matrix]);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/crm/roles"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Link>
        </Button>
        <h1 className="text-xl font-semibold text-foreground">
          Permission Matrix{role ? `: ${role.role_name}` : ''}
        </h1>
        {canEdit && (
          <Button className="ml-auto" size="sm" onClick={save} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save Permissions'}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Modules × Actions</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && (
            <table className="w-full text-sm border-collapse min-w-[640px]">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2 pr-3 font-medium">Module</th>
                  {actionCols.map((a) => (
                    <th key={a} className="py-2 px-2 font-medium capitalize text-center">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.map((mod) => (
                  <tr key={mod.module_name} className="border-b border-border/60">
                    <td className="py-2 pr-3 capitalize text-foreground">{mod.module_name.replace(/_/g, ' ')}</td>
                    {actionCols.map((a) => {
                      const cell = mod.actions?.[a];
                      if (!cell) {
                        return <td key={a} className="text-center text-muted-foreground">—</td>;
                      }
                      const checked = selected.has(Number(cell.permission_id));
                      return (
                        <td key={a} className="text-center py-2 px-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canEdit}
                            onChange={() => toggle(Number(cell.permission_id))}
                            title={cell.display_name}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
